import React, { useState, useMemo } from 'react';
import { useProducts } from '../ProductContext';
import { apiJson } from '../apiClient';
import { useAuth } from '../AuthContext';
import { isBrickFactoryUser } from '../utils/businessType';
import { isFactoryRawMaterialCategory } from '../constants/factoryStockCategories';
import './LowStockOrderForm.css';

export default function LowStockOrderForm() {
  const { user } = useAuth();
  const factoryMode = isBrickFactoryUser(user);
  const { products, inventoryLoading, refreshProducts } = useProducts();
  const [selected, setSelected] = useState(() => new Set());
  const [quantities, setQuantities] = useState({});
  const [unitCosts, setUnitCosts] = useState({});
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [neededByDate, setNeededByDate] = useState('');
  const [priority, setPriority] = useState('normal');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const lowStockProducts = useMemo(() => {
    const list = (products || []).filter((p) => {
      if (factoryMode && !isFactoryRawMaterialCategory(p.category)) return false;
      const q = Number(p.quantity) || 0;
      const lim = Number(p.lowStockLimit) || 0;
      return q <= lim;
    });
    return [...list].sort((a, b) => (Number(a.quantity) || 0) - (Number(b.quantity) || 0));
  }, [products, factoryMode]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleQuantity = (id, value) => {
    setQuantities((prev) => ({ ...prev, [id]: value }));
  };

  const handleUnitCost = (id, value) => {
    setUnitCosts((prev) => ({ ...prev, [id]: value }));
  };

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiJson('/suppliers');
        if (!mounted) return;
        setSuppliers(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!mounted) return;
        setSuppliers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const estimatedGrandTotal = useMemo(() => {
    return [...selected].reduce((sum, id) => {
      const qty = Math.max(1, parseInt(String(quantities[id]), 10) || 0);
      const unit = Math.max(0, Number(unitCosts[id]) || 0);
      return sum + qty * unit;
    }, 0);
  }, [selected, quantities, unitCosts]);

  const composedLowStockNotes = () => {
    const lines = ['Low stock reorder from inventory list'];
    if (neededByDate) lines.push(`Needed by: ${neededByDate}`);
    if (paymentTerms.trim()) lines.push(`Payment terms: ${paymentTerms.trim()}`);
    if (paymentDueDate) lines.push(`Payment due: ${paymentDueDate}`);
    lines.push(`Priority: ${priority}`);
    lines.push(`Estimated total (TZS): ${Math.round(estimatedGrandTotal).toLocaleString()}`);
    return lines.join(' | ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    const ids = [...selected];
    if (ids.length === 0) {
      setError('Select at least one product.');
      setSubmitting(false);
      return;
    }

    const lines = [];
    for (const id of ids) {
      const p = lowStockProducts.find((x) => String(x.id) === String(id));
      if (!p) continue;
      const raw = quantities[id];
      const qty = parseInt(String(raw), 10);
      if (!Number.isFinite(qty) || qty < 1) {
        setError(`Enter a valid order quantity for "${p.name}".`);
        setSubmitting(false);
        return;
      }
      const onHand = Number(p.quantity) || 0;
      const unit = Number(unitCosts[id]);
      if (factoryMode && (!Number.isFinite(unit) || unit <= 0)) {
        setError(`Enter purchase price (TZS) for "${p.name}".`);
        setSubmitting(false);
        return;
      }
      lines.push({
        productId: p.id,
        product_name: p.name,
        category: p.category || '',
        quantity: qty,
        estimatedUnitCostTzs: Math.max(0, unit || 0),
        reason: `Low stock reorder (on hand: ${onHand}, limit: ${Number(p.lowStockLimit) || 0})`,
      });
    }

    if (lines.length === 0) {
      setError('Nothing to submit.');
      setSubmitting(false);
      return;
    }

    try {
      await apiJson('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: lines,
          supplierId: supplierId ? Number(supplierId) : undefined,
          supplierName: supplierName.trim() || undefined,
          supplierPhone: supplierPhone.trim() || undefined,
          neededByDate: neededByDate || undefined,
          priority,
          paymentTerms: paymentTerms.trim() || undefined,
          paymentDueDate: paymentDueDate || undefined,
          notes: composedLowStockNotes(),
        }),
      });
      setSuccess(true);
      setSelected(new Set());
      setQuantities({});
      setUnitCosts({});
      setSupplierId('');
      setSupplierName('');
      setSupplierPhone('');
      setNeededByDate('');
      setPriority('normal');
      setPaymentTerms('');
      setPaymentDueDate('');
      await refreshProducts();
    } catch (err) {
      setError(err.body?.message || err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (inventoryLoading) {
    return (
      <div className="low-stock-form low-stock-form--loading">
        <p className="low-stock-loading">Loading inventory…</p>
      </div>
    );
  }

  if (lowStockProducts.length === 0) {
    return (
      <div className="low-stock-form low-stock-form--empty">
        <h3 className="low-stock-title">{factoryMode ? 'Raw materials reorder' : 'Low stock reorder'}</h3>
        <p className="low-stock-empty-text">
          {factoryMode
            ? 'No raw materials are at or below their low-stock threshold. Use “Order materials” to buy from your catalog, or add materials and set alert levels first.'
            : 'No products are currently at or below their low-stock threshold. Thresholds are set when products are added to stock.'}
        </p>
      </div>
    );
  }

  return (
    <form className="low-stock-form" onSubmit={handleSubmit}>
      <h3 className="low-stock-title">{factoryMode ? 'Raw materials reorder' : 'Low stock reorder'}</h3>
      <p className="low-stock-lead">
        {factoryMode
          ? 'Select raw materials at or below their low-stock limit. Enter order quantity and purchase price (TZS). Selling price is set when the owner receives the order.'
          : 'Select approved products that are at or below their low-stock limit. Enter how many units you want to order (can be more than current on-hand). The owner approves the request; stock is not changed here.'}
      </p>
      <div className="low-stock-proc-grid">
        <select
          className="low-stock-proc-input"
          value={supplierId}
          onChange={(e) => {
            const id = e.target.value;
            setSupplierId(id);
            const chosen = suppliers.find((s) => String(s.id) === String(id));
            if (chosen) {
              setSupplierName(chosen.name || '');
              setSupplierPhone(chosen.phone || '');
            }
          }}
        >
          <option value="">Select supplier (optional)</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}{s.phone ? ` — ${s.phone}` : ''}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="low-stock-proc-input"
          placeholder="Supplier name (optional)"
          value={supplierName}
          onChange={(e) => setSupplierName(e.target.value)}
        />
        <input
          type="text"
          className="low-stock-proc-input"
          placeholder="Supplier phone"
          value={supplierPhone}
          onChange={(e) => setSupplierPhone(e.target.value)}
        />
        <input
          type="date"
          className="low-stock-proc-input"
          value={neededByDate}
          onChange={(e) => setNeededByDate(e.target.value)}
        />
        <select
          className="low-stock-proc-input"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="low">Priority: Low</option>
          <option value="normal">Priority: Normal</option>
          <option value="high">Priority: High</option>
          <option value="urgent">Priority: Urgent</option>
        </select>
        <input
          type="text"
          className="low-stock-proc-input"
          placeholder="Payment terms (optional)"
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
        />
        <input
          type="date"
          className="low-stock-proc-input"
          value={paymentDueDate}
          onChange={(e) => setPaymentDueDate(e.target.value)}
        />
      </div>

      <div className="low-stock-table-wrap">
        <table className="low-stock-table">
          <thead>
            <tr>
              <th className="low-stock-th-check" scope="col">
                Select
              </th>
              <th scope="col">Product</th>
              <th scope="col">Category</th>
              <th scope="col">On hand</th>
              <th scope="col">Limit</th>
              <th scope="col">{factoryMode ? 'Purchase price (TZS)' : 'Unit cost (TZS)'}</th>
              <th scope="col">Order qty</th>
              <th scope="col">Line total</th>
            </tr>
          </thead>
          <tbody>
            {lowStockProducts.map((item) => {
              const id = item.id;
              const checked = selected.has(id);
              const onHand = Number(item.quantity) || 0;
              return (
                <tr key={id} className={checked ? 'low-stock-row low-stock-row--selected' : 'low-stock-row'}>
                  <td>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(id)}
                      aria-label={`Select ${item.name}`}
                    />
                  </td>
                  <td className="low-stock-name">{item.name}</td>
                  <td className="low-stock-muted">{item.category || '—'}</td>
                  <td>
                    <span className={onHand === 0 ? 'low-stock-alert' : ''}>{onHand}</span>
                  </td>
                  <td className="low-stock-muted">{Number(item.lowStockLimit) || 0}</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      className="low-stock-qty-input"
                      value={unitCosts[id] ?? ''}
                      onChange={(e) => handleUnitCost(id, e.target.value)}
                      disabled={!checked}
                      aria-label={`Unit cost for ${item.name}`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      className="low-stock-qty-input"
                      value={quantities[id] ?? ''}
                      onChange={(e) => handleQuantity(id, e.target.value)}
                      disabled={!checked}
                      aria-label={`Order quantity for ${item.name}`}
                    />
                  </td>
                  <td className="low-stock-muted">
                    {checked
                      ? Math.round((Math.max(1, parseInt(String(quantities[id]), 10) || 0) * (Math.max(0, Number(unitCosts[id]) || 0)))).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: 10, fontWeight: 700, color: '#0f172a' }}>
        Estimated total: TZS {Math.round(estimatedGrandTotal).toLocaleString()}
      </div>

      <button type="submit" className="low-stock-submit" disabled={submitting || selected.size === 0}>
        {submitting ? 'Submitting…' : 'Submit low stock order'}
      </button>

      {error && (
        <div className="low-stock-alert-box" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="low-stock-success" role="status">
          Order submitted. Check the order list for status.
        </div>
      )}
    </form>
  );
}
